"use client"

import * as React from "react"
import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { createPortal } from "react-dom"
import { Search, X, ChevronDown, Check, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { Input } from "./input"
import { Button } from "./button"

// --- Types ---

export interface SearchableSelectOption {
  id: string
  name: string
  description?: string
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[]
  value?: string
  onValueChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  disabled?: boolean
  isLoading?: boolean
  filterFn?: (option: SearchableSelectOption, searchTerm: string) => boolean
}

// --- Sub-components ---

const OptionItem = ({
  option,
  isSelected,
  isHighlighted,
  onSelect,
  onMouseEnter,
}: {
  option: SearchableSelectOption
  isSelected: boolean
  isHighlighted: boolean
  onSelect: () => void
  onMouseEnter: () => void
}) => (
  <div
    onMouseDown={(e) => {
      // Forzamos la ejecución inmediata y prevenimos conflictos de foco
      e.preventDefault()
      e.stopPropagation()
      onSelect()
    }}
    onMouseEnter={onMouseEnter}
    className={cn(
      "relative flex flex-col cursor-pointer select-none items-start rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
      isHighlighted || isSelected
        ? "bg-primary/10 text-primary"
        : "hover:bg-primary/5"
    )}
  >
    <div className="flex items-center justify-between w-full">
      <span className="flex-1 truncate">{option.name}</span>
      {isSelected && <Check className="h-4 w-4 ml-2 shrink-0" />}
    </div>
    {option.description && (
      <span className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
        {option.description}
      </span>
    )}
  </div>
)

// --- Main Component ---

export const SearchableSelect = React.forwardRef<
  HTMLDivElement,
  SearchableSelectProps
>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = "Select an option...",
      searchPlaceholder = "Search...",
      emptyMessage = "No options found",
      className,
      disabled = false,
      isLoading = false,
      filterFn,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [selectedIndex, setSelectedIndex] = useState(-1)
    
    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLDivElement>(null)

    // Connect forwarded ref to containerRef
    React.useImperativeHandle(ref, () => containerRef.current as HTMLDivElement)

    const selectedOption = useMemo(() => options.find((opt) => opt.id === value), [options, value])

    // Filter logic
    const filteredOptions = useMemo(() => {
      const normalizedTerm = searchTerm.toLowerCase().trim()
      if (!normalizedTerm) return options

      const defaultFilterFn = (option: SearchableSelectOption, term: string) => {
        return (
          option.name.toLowerCase().includes(term) ||
          (option.description && option.description.toLowerCase().includes(term))
        )
      }

      const filterFunction = filterFn || defaultFilterFn
      return options.filter((option) => filterFunction(option, normalizedTerm))
    }, [options, searchTerm, filterFn])

    // Reset index when filtering
    useEffect(() => {
      setSelectedIndex(-1)
    }, [filteredOptions])

    const handleSelect = useCallback((optionId: string) => {
      onValueChange(optionId)
      setIsOpen(false)
      setSearchTerm("")
      // No hacemos blur aquí para evitar problemas de sincronización, 
      // el input ya perderá el foco si es necesario o lo mantendrá si el usuario sigue interactuando
    }, [onValueChange])

    const handleClose = useCallback(() => {
      setIsOpen(false)
      setSearchTerm("")
    }, [])

    // Keyboard navigation
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (!isOpen) return

        switch (e.key) {
          case "ArrowDown":
            e.preventDefault()
            setSelectedIndex((prev) =>
              prev < filteredOptions.length - 1 ? prev + 1 : prev
            )
            break
          case "ArrowUp":
            e.preventDefault()
            setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
            break
          case "Enter":
            e.preventDefault()
            if (selectedIndex >= 0 && filteredOptions[selectedIndex]) {
              handleSelect(filteredOptions[selectedIndex].id)
            }
            break
          case "Escape":
            e.preventDefault()
            handleClose()
            inputRef.current?.blur()
            break
        }
      }

      window.addEventListener("keydown", handleKeyDown)
      return () => window.removeEventListener("keydown", handleKeyDown)
    }, [isOpen, selectedIndex, filteredOptions, handleSelect, handleClose])

    // Scroll into view
    useEffect(() => {
      if (isOpen && selectedIndex >= 0 && listRef.current) {
        const selectedElement = listRef.current.children[selectedIndex] as HTMLElement
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: "nearest", behavior: "smooth" })
        }
      }
    }, [selectedIndex, isOpen])

    // Click outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node
        if (
          containerRef.current && !containerRef.current.contains(target) &&
          dropdownRef.current && !dropdownRef.current.contains(target)
        ) {
          handleClose()
        }
      }

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
      }
      return undefined
    }, [isOpen, handleClose])

    // Position calculation
    const [dropdownPosition, setDropdownPosition] = useState<{
      top: number
      left: number
      width: number
    } | null>(null)

    useEffect(() => {
      if (isOpen && containerRef.current) {
        const updatePosition = () => {
          if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect()
            setDropdownPosition({
              top: rect.bottom + window.scrollY + 4,
              left: rect.left + window.scrollX,
              width: rect.width,
            })
          }
        }
        updatePosition()
        window.addEventListener('scroll', updatePosition, true)
        window.addEventListener('resize', updatePosition)
        return () => {
          window.removeEventListener('scroll', updatePosition, true)
          window.removeEventListener('resize', updatePosition)
        }
      }
      return undefined
    }, [isOpen])

    const handleClearSelection = (e: React.MouseEvent) => {
      e.stopPropagation()
      onValueChange("")
      setSearchTerm("")
    }

    return (
      <>
        <div ref={containerRef} className={cn("relative", className)}>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
            <Input
              ref={inputRef}
              value={isOpen ? searchTerm : selectedOption?.name || ""}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setIsOpen(true)
              }}
              onFocus={() => !disabled && !isLoading && setIsOpen(true)}
              placeholder={isOpen ? searchPlaceholder : placeholder}
              disabled={disabled || isLoading}
              className={cn("pl-9 pr-10", value && !isOpen && "pr-10")}
            />
            <div className="absolute right-1 top-1 flex items-center gap-0.5">
              {isOpen && searchTerm && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => {
                    setSearchTerm("")
                    inputRef.current?.focus()
                  }}
                  type="button"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
              {!isOpen && value && !disabled && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleClearSelection}
                  type="button"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
              <ChevronDown className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-200 mr-1",
                isOpen && "rotate-180"
              )} />
            </div>
          </div>
        </div>

        {isOpen && dropdownPosition && typeof document !== 'undefined' && createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[2200] flex flex-col overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 pointer-events-auto"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              maxHeight: '300px'
            }}
          >
            <div className="overflow-y-auto overflow-x-hidden p-1 custom-scrollbar">
              {isLoading ? (
                <div className="py-8 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading...</span>
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="py-8 text-center text-xs text-muted-foreground">
                  {emptyMessage}
                </div>
              ) : (
                <div ref={listRef} className="flex flex-col gap-0.5">
                  {filteredOptions.map((option, index) => (
                    <OptionItem
                      key={option.id}
                      option={option}
                      isSelected={option.id === value}
                      isHighlighted={index === selectedIndex}
                      onSelect={() => handleSelect(option.id)}
                      onMouseEnter={() => setSelectedIndex(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
      </>
    )
  }
)

SearchableSelect.displayName = "SearchableSelect"
